import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { CandidateOnboardingFlow } from './CandidateOnboarding';
import { clearHomeCandidate, loadHomeCandidate } from './candidateHomeProfile';

export const ONBOARDING_MODAL_SKIP_KEY = 'hv_onboarding_modal_skipped';
export const CANDIDATE_PROFILE_UPDATED_EVENT = 'candidate-profile-updated';

const ONBOARDING_REQUIRED_FIELDS = [
  { key: 'experience_yrs', label: 'Years of experience' },
  { key: 'noticePeriod', label: 'Notice period' },
  { key: 'compensation', label: 'Current CTC' },
  { key: 'expectation', label: 'Expected CTC' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'resume', label: 'Resume' },
] as const;

type OnboardingRequiredFieldKey = (typeof ONBOARDING_REQUIRED_FIELDS)[number]['key'];

interface CandidateOnboardingProfile {
  experience_yrs?: number | null;
  noticePeriod?: number | null;
  compensation?: number | null;
  expectation?: string | null;
  linkedin?: string | null;
  resume?: string | null;
}

interface GateUser {
  role?: string;
  email?: string;
}

const ALL_REQUIRED: OnboardingRequiredFieldKey[] = ONBOARDING_REQUIRED_FIELDS.map((f) => f.key);

function stripLakh(v: unknown) {
  if (v === undefined || v === null || v === '') return '';
  return String(v).replace(/\s*l\s*$/i, '').trim();
}

function isOnboardingFieldMissing(
  candidate: CandidateOnboardingProfile | null | undefined,
  field: OnboardingRequiredFieldKey,
): boolean {
  if (!candidate) return true;

  switch (field) {
    case 'experience_yrs':
      return candidate.experience_yrs == null || candidate.experience_yrs === ('' as unknown as number);
    case 'noticePeriod':
      return candidate.noticePeriod == null || candidate.noticePeriod === ('' as unknown as number);
    case 'compensation': {
      const c = candidate.compensation;
      return c == null || c === ('' as unknown as number) || c === 0;
    }
    case 'expectation': {
      const e = stripLakh(candidate.expectation);
      return !e || e === '0';
    }
    case 'linkedin':
      return !String(candidate.linkedin || '').trim();
    case 'resume':
      return !String(candidate.resume || '').trim();
    default:
      return false;
  }
}

function getMissingOnboardingFields(
  candidate: CandidateOnboardingProfile | null | undefined,
): OnboardingRequiredFieldKey[] {
  return ONBOARDING_REQUIRED_FIELDS
    .map((f) => f.key)
    .filter((key) => isOnboardingFieldMissing(candidate, key));
}

function isOnboardingRequired(
  candidate: CandidateOnboardingProfile | null | undefined,
): boolean {
  if (!candidate) return true;
  return getMissingOnboardingFields(candidate).length > 0;
}

export function useCandidateOnboardingGate(user: GateUser | null | undefined) {
  const [loading, setLoading] = useState(true);
  const [required, setRequired] = useState(false);
  const [missingFields, setMissingFields] = useState<OnboardingRequiredFieldKey[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handleRefresh = () => {
      if (user?.email) clearHomeCandidate(user.email);
      setRefreshKey((k) => k + 1);
    };
    window.addEventListener(CANDIDATE_PROFILE_UPDATED_EVENT, handleRefresh);
    return () => window.removeEventListener(CANDIDATE_PROFILE_UPDATED_EVENT, handleRefresh);
  }, [user?.email]);

  useEffect(() => {
    if (user?.role !== 'candidate' || !user?.email) {
      setLoading(false);
      setRequired(false);
      setMissingFields([]);
      return;
    }

    let active = true;
    setLoading(true);

    loadHomeCandidate(user.email)
      .then((res) => {
        if (!active) return;
        if (!res.ok) {
          console.warn('[onboarding-gate] candidate profile fetch failed', res.error);
          setRequired(true);
          setMissingFields(ALL_REQUIRED);
          return;
        }
        const candidate = res.data as CandidateOnboardingProfile;
        setRequired(isOnboardingRequired(candidate));
        setMissingFields(getMissingOnboardingFields(candidate));
      })
      .catch((err) => {
        if (!active) return;
        console.warn('[onboarding-gate] candidate profile fetch failed', err?.message || err);
        setRequired(true);
        setMissingFields(ALL_REQUIRED);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [user?.role, user?.email, refreshKey]);

  return { loading, required, missingFields };
}

interface CandidateOnboardingModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CandidateOnboardingModal({ open, onClose }: CandidateOnboardingModalProps) {
  if (!open) return null;

  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[200] bg-[var(--overlay-scrim)] backdrop-blur-[2px]" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[201] flex max-h-[90vh] w-[min(680px,95vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-xl outline-none"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-[var(--border-default)] px-5 py-3">
            <Dialog.Title className="text-sm font-semibold text-[var(--text-primary)]">
              Complete your profile
            </Dialog.Title>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-2 py-1 text-sm font-medium text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)]"
            >
              Skip
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
            <CandidateOnboardingFlow onClose={onClose} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
